import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  MRT_ToggleGlobalFilterButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import { PageTab } from "users/networking/_views/network-page-tabs";
import {
  useAcceptNetworkInvitationMutation,
  useCancelNetworkInvitationMutation,
  useGetUserNetworkInvitationsQuery,
  useRejectNetworkInvitationMutation,
} from "users/networking/connections/invitations/_api/network-invitation-api";
import NetworkInvitationQuery, {
  NetworkInvitationFilterParameters,
  NetworkInvitationOrderParameters,
} from "users/networking/connections/invitations/_api/queries/network-invitation-query";
import NetworkInvitationInfo from "users/networking/connections/invitations/network-invitation-info";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const FilterButtons = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
  "& > .MuiChip-root": {
    [theme.breakpoints.down("xl")]: {
      marginBottom: theme.spacing(0.5),
    },
    marginLeft: theme.spacing(0.5),
  },
}));

export default function PendingTab() {
  const [sent, setSent] = React.useState(false);
  const [received, setReceived] = React.useState(false);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const session = useSession();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // #region Queries
  const [acceptInvitation, acceptInvitationResult] = useAcceptNetworkInvitationMutation();
  const [cancelInvitation, cancelInvitationResult] = useCancelNetworkInvitationMutation();
  const [rejectInvitation, rejectInvitationResult] = useRejectNetworkInvitationMutation();

  const createdDate = columnFilters.find((filter) => filter.id === "createdDate")?.value as string;
  const paginationParameters = PaginationParameters.create(pagination);
  const filterParameters = new NetworkInvitationFilterParameters({
    name: columnFilters.find((filter) => filter.id === "name")?.value as string,
    status: columnFilters.find((filter) => filter.id === "status")?.value as string,
    message: columnFilters.find((filter) => filter.id === "message")?.value as string,
    sentDate: createdDate ? new AHDate(createdDate) : undefined,
  });

  const orderParameters = new NetworkInvitationOrderParameters();
  // (sorting[0]?.id as NetworkInvitationOrderField) ?? NetworkInvitationOrderField.Name,
  // sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending

  const invitationQuery = new NetworkInvitationQuery({
    filterParameters,
    orderParameters,
    paginationParameters,
    query: globalFilter,
  });

  const networkInvitationQuery = useGetUserNetworkInvitationsQuery(invitationQuery);

  const invitationUserIds = useMemo(() => {
    const queryResult = networkInvitationQuery.data;
    if (!queryResult) return new Set<string>();
    return new Set(queryResult.data.map((invitation) => invitation.user.id));
  }, [networkInvitationQuery.data]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    invitationUserIds?.size ? { userIds: invitationUserIds } : skipToken,
  );

  const networkInvitations = useMemo(() => {
    const queryResult = networkInvitationQuery.data;

    if (!queryResult) {
      return PaginatedResponse.empty<NetworkInvitationInfo>();
    }

    const data = queryResult.data.filter((invitation) => {
      const context = searchParams.get("context");
      if (context === "sent" && invitation.inviter) return true;
      return context === "received" && !invitation.inviter;
    });

    return PaginatedResponse.from<NetworkInvitationInfo>(
      NetworkInvitationInfo.fromJSONArray(data), 
      queryResult.headers,
    );
  }, [networkInvitationQuery.data, searchParams]);

  useEffect(() => {
    // Handle URL changes
    if (routeParams.action === "view" && routeParams.id) {
      handleViewProfile(new Guid(routeParams.id));
    }

    let context = searchParams.get("context");
    if (context === null) {
      setSearchParams((prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("context", "received");
        return params;
      });
      return;
    }

    setSent(context === "sent");
    setReceived(context === "received");
  }, [routeParams, searchParams]);

  const renderPendingNameColumn = useCallback(
    (invitation: NetworkInvitationInfo) => {
      const individualJson = individualQuery.data?.find((ind) => ind.userId === invitation.user.id.value);
      const individual = Individual.fromJSON(individualJson);
      return (
        <NameCellContainer>
          <IndividualAvatar individual={individual ?? undefined} session={session} />
          <RowTitle>{invitation.user?.name.fullName}</RowTitle>
        </NameCellContainer>
      );
    },
    [session, individualQuery.data],
  );

  const columnHelper = createMRTColumnHelper<NetworkInvitationInfo>();

  const pendingColumns = useMemo(
    () => [
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: ({ row }) => renderPendingNameColumn(row.original),
      }),
      columnHelper.display({
        header: "Sent",
        id: "createdDate",
        Cell: ({ row }) => {
          const created = row.original.created;
          if (created instanceof AHDate) return created.format("MM/DD/YYYY");
          return created;
        },
      }),
      columnHelper.display({
        header: "Message",
        id: "message",
        Cell: ({ row }) => {
          const message = row.original.message ?? <Icon>remove</Icon>;
          return message;
        },
      }),
      columnHelper.display({
        header: "Status",
        id: "status",
        Cell: ({ row }) => row.original.status,
      }),
    ],
    [columnHelper],
  );

  async function handleAcceptInvitation(invitationId: Guid): Promise<void> {
    if (!invitationId) {
      await Promise.reject(new Error("No network invitation id provided"));
      return;
    }

    try {
      await acceptInvitation({ invitationId });

      if (acceptInvitationResult.isError) {
        throw acceptInvitationResult.error;
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to accept invitation", { variant: "error" });
    }
  }

  async function handleRejectInvitation(invitationId: Guid): Promise<void> {
    try {
      if (!invitationId) {
        throw new Error("No network invitation id provided");
      }

      await rejectInvitation({ invitationId });

      if (rejectInvitationResult.isError) {
        throw rejectInvitationResult.error;
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to reject invitation", { variant: "error" });
    }
  }

  async function handleCancelInvitation(invitationId: Guid): Promise<void> {
    try {
      if (!invitationId) {
        await Promise.reject(new Error("No network invitation id provided"));
        return;
      }

      const response = await confirm({
        title: "Cancel Network Connection Invitation?",
        message: "Are you sure you want to cancel this network connection invitation?",
        okButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (response === ConfirmResponse.Cancel) return;

      await cancelInvitation({ invitationId });

      if (cancelInvitationResult.isError) {
        throw cancelInvitationResult.error;
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to cancel invitation", { variant: "error" });
    }
  }

  function handleViewProfile(individualId?: Guid, data?: NetworkInvitationInfo): void {
    if (!individualId) return;
    let individualProfile: IndividualProfile | undefined;
    if (data instanceof UserInformation) {
      individualProfile = new IndividualProfile({
        userId: data.id,
        firstName: data.name.firstName ?? "",
        lastName: data.name.lastName ?? "",
        avatarId: data.avatarId,
        legalEntityId: data.legalEntityId,
        email: data.email,
      });
    } else if (data instanceof NetworkInvitationInfo) {
      individualProfile = new IndividualProfile({
        userId: data.user.id,
        firstName: data.user.name.firstName ?? "",
        lastName: data.user.name.lastName ?? "",
        avatarId: data.avatarId,
        email: data.user.email ?? undefined,
      });
    }

    openDialog({
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: false,
      },
      component: <ViewIndividualProfile individualId={individualId} profile={individualProfile} />,
    });
  }

  function getPendingTabActions(row: Row<NetworkInvitationInfo>) {
    const invitation = row.original;
    if (invitation?.status === "accepted" || invitation?.status === "rejected") {
      return null;
    }
    return (
      <>
        {invitation.inviter ? (
          <Tooltip title="Cancel Invitation">
            <span>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  void handleCancelInvitation(invitation.id);
                }}
              >
                <CancelIcon color="error" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Accept Invitation">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleAcceptInvitation(invitation.id);
                  }}
                >
                  <CheckIcon color="success" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reject Invitation">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleRejectInvitation(invitation.id);
                  }}
                >
                  <CloseIcon color="error" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </>
    );
  }

  const table = useMaterialReactTable({
    columns: pendingColumns,
    data: networkInvitations.data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => {
      return <RowActionsContainer>{getPendingTabActions(row)}</RowActionsContainer>;
    },
    renderTopToolbarCustomActions: () => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Network</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={PageTab.Pending}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => {
                navigate(`/network/${newValue}`);
              }}
            >
              <Tab value={PageTab.Contacts} label="Contacts" />
              <Tab value={PageTab.Coworkers} label="Coworkers" />
              <Tab value={PageTab.Pending} label="Pending" />
              <Tab value={PageTab.Groups} label="Groups" />
            </Tabs>
            <FilterButtons>
              <Chip
                icon={<FilterListIcon />}
                label="Received"
                color="primary"
                variant={received ? "filled" : "outlined"}
                onClick={() => {
                  if (received) return;
                  setReceived((prevValue) => !prevValue);
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    if (received) {
                      params.set("context", "sent");
                    } else {
                      params.set("context", "received");
                    }
                    return params;
                  });
                }}
              />
              <Chip
                icon={<FilterListIcon />}
                label="Sent"
                color="primary"
                variant={sent ? "filled" : "outlined"}
                onClick={() => {
                  if (sent) return;
                  setSent((prevValue) => !prevValue);
                  setSearchParams((prevParams) => {
                    const params = new URLSearchParams(prevParams);
                    if (sent) {
                      params.set("context", "received");
                    } else {
                      params.set("context", "sent");
                    }
                    return params;
                  });
                }}
              />
            </FilterButtons>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    initialState: {
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate(`/network/pending/view/${row.original.individualId}`);
      },
      sx: { cursor: "pointer" },
    }),
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: networkInvitationQuery.isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    rowCount: networkInvitations.totalElements,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      globalFilter,
      columnFilters,
      isLoading: networkInvitationQuery.isLoading,
      pagination,
      showAlertBanner: networkInvitationQuery.isError,
      showProgressBars: networkInvitationQuery.isFetching,
      showSkeletons: networkInvitationQuery.isLoading,
      sorting,
    },
  });

  return <MaterialReactTable table={table} />;
}
